import React from "react";
import { useState, useCallback, useEffect } from "react";
import { CloudUpload, Upload } from "lucide-react";
import { useDropzone } from "react-dropzone";

import { Header } from "../../Header";
import { postBackgroundRemove } from "../../../api/apiClient";
import { BackgroundRemoverResult } from "../ResponsePage/BackgroundRemoverResult";
import { GlobalDragOverlay } from "../../../utils/GlobalDragOverlay";

import { useAuth } from "../../../AuthProvider";

export const BackgroundRemover = () => {
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { currentUser } = useAuth();

  const processImage = async (imageData) => {
    try {
      const base64String = imageData.split(",")[1];
      setLoading(true);
      setIsRedirecting(true);

      const headers = {};
      if (currentUser) {
        headers["Authorization"] = `Bearer ${await currentUser.getIdToken()}`;
        headers["X-User-Id"] = currentUser.uid;
      }

      const response = await postBackgroundRemove({
        image: base64String,
        headers,
      });

      if (response.data.image) {
        setProcessedImage(`data:image/png;base64,${response.data.image}`);
      }else if(response.data && response.data.error_code === 2){
          // Specific handling for plan upgrade prompt
          alert("You have exceeded your current plan's monthly limits for background removal usage. Please upgrade your plan.");
          setLoading(false);
          setIsRedirecting(false);
      }
    } catch (error) {
      console.error("Error processing image:", error);
      alert("Error processing image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const imageUrl = reader.result;
        setImage(imageUrl);
        processImage(imageUrl);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);

    return () => {
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
    };
  }, []);

  const handleGlobalDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0].type.startsWith("image/")) {
      const reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = () => {
        const imageUrl = reader.result;
        setImage(imageUrl);
        processImage(imageUrl);
      };
    }
  };

  const handleNewUpload = () => {
    setProcessedImage(null);
    setImage(null);
  };

  if (isRedirecting || processedImage) {
    return (
      <BackgroundRemoverResult
        processedImage={processedImage}
        originalImage={image}
        isLoading={loading}
        onBackClick={handleNewUpload}
        onNewUpload={handleNewUpload}
      />
    );
  }

  return (
    <GlobalDragOverlay onGlobalDrop={handleGlobalDrop}>
      <div className="flex-1 h-full overflow-auto relative z-10 bg-gradient-to-b from-slate-50 to-white">
        <Header title={"Background Remover"} />

        {/* Upload Section */}
        <section id="try-now" className="py-5 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className=" mb-5">
              <h2 className="inline-block  text-[24px] text-xs text-[#121212]">
                Remove Distractions Instantly
              </h2>

              <p className="text-base sm:text-lg text-[#697586]">
                Effortlessly clean up your visuals by removing unwanted
                backgrounds in seconds—perfect for product shots, portraits, or
                social media posts.
              </p>
            </div>

            <div>
              <div className="w-full bg-[#FFFFFF] h-[600px] rounded-[16px] border-[1px] border-[#EEF2F6] overflow-hidden p-4">
                <div className="flex items-center justify-start gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 sm:h-[32px] sm:w-[32px] text-indigo-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 mt-4">
                    Remove Background in seconds
                  </h3>
                </div>

                <div
                  className={`w-full border-2 flex items-center flex-col h-[500px] justify-center gap-4 bg-[#F8FAFC] ${
                    isDragActive
                      ? "border-[#CDD5DF] bg-indigo-50"
                      : "border-[#CDD5DF]"
                  } border-dashed rounded-lg transition-all duration-300 p-6 sm:p-8 md:p-12`}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />

                  {image ? (
                    <div className="flex flex-col items-center gap-4 sm:gap-4 w-full h-full">
                      <div className="relative group flex-1 w-full flex items-center justify-center">
                        <img
                          src={image}
                          alt="Uploaded"
                          className="max-w-full max-h-64 sm:max-h-80 md:max-h-96 object-contain rounded-lg shadow-md"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300 rounded-lg">
                          <p className="text-white font-medium text-sm sm:text-base">
                            Processing this image...
                          </p>
                        </div>
                      </div>

                      {loading ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-t-2 border-b-2 border-indigo-500"></div>
                          <p className="text-sm sm:text-base text-gray-600">
                            Removing background...
                          </p>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setImage(null);
                          }}
                          className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-2 sm:px-8 sm:py-3 rounded-full hover:shadow-lg hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 text-sm sm:text-base"
                        >
                          Upload Another Image
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-center p-1 w-[40px] text-[#697586] h-[40px] bg-white rounded-md">
                        <CloudUpload size={24} />
                      </div>

                      <h3 className="text-md sm:text-lg font-medium text-gray-800 text-center">
                        {isDragActive
                          ? "Drop your image here"
                          : "Drag and drop a photo file here to upload"}
                      </h3>

                      <button className="bg-[#FFFFFF] text-gray-800 px-4 py-2 flex items-center justify-center gap-2 rounded-[8px] text-sm sm:text-base font-medium border-[1px] border-[#CDD5DF] transition-all duration-300 transform hover:-translate-y-1">
                        <Upload size={16} /> <span>Upload Image</span>
                      </button>

                      <p className="text-xs sm:text-sm text-gray-500 text-center max-w-md">
                        Supported file formats: JPG, PNG, GIF, WebP- Maximum
                        file size: 5 MB
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </GlobalDragOverlay>
  );
};
